/**
 * Reminders a parent has closed, remembered on this device (2026-10-13).
 *
 * Keyed by what the reminder SAYS, not just which kind it is: closing "A
 * device is asking" hides that one, and a second device asking brings it
 * back, because the words changed. Browser storage only -- a convenience, so
 * a blocked or cleared store simply shows the reminder again.
 */
import { useCallback, useState } from 'react';

const PREFIX = 'birbly.dismissed.';

function read(scope: string): string[] {
  try {
    const raw = localStorage.getItem(PREFIX + scope);
    const list: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function write(scope: string, list: string[]): void {
  try {
    // Only the most recent few: an old signature never comes back anyway.
    localStorage.setItem(PREFIX + scope, JSON.stringify(list.slice(-40)));
  } catch {
    /* storage blocked: the reminder just comes back next time */
  }
}

/** `isDismissed(signature)` and `dismiss(signature)`, for one family. */
export function useDismissed(scope: string): {
  isDismissed: (signature: string) => boolean;
  dismiss: (signature: string) => void;
} {
  const [list, setList] = useState<string[]>(() => read(scope));
  const dismiss = useCallback(
    (signature: string) => {
      setList((current) => {
        const next = current.includes(signature) ? current : [...current, signature];
        write(scope, next);
        return next;
      });
    },
    [scope],
  );
  const isDismissed = useCallback((signature: string) => list.includes(signature), [list]);
  return { isDismissed, dismiss };
}
